'use client'

import { useEffect, useState, useRef } from 'react'
// Remove the legacy imports
// import { useWebSocketListener } from '@/lib/hooks/use-websocket'
import useNotificationStore from '@/lib/hooks/zustand-stores/use-notifications-store'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import { Notification } from '@/lib/notifications/notification-types'
import { useLogger } from '@/lib/hooks/useLogger'
// Remove WebSocketManager import
import useWSMessage from '@/lib/hooks/use-ws-message'
import useWebSocketStore, { WebSocketMsg } from '@/lib/hooks/zustand-stores/use-websocket-store'

// How often to poll for notifications when WebSocket is disconnected
const FALLBACK_POLLING_INTERVAL = 30000; // 30 seconds
// How long to wait for WebSocket connection before falling back to API
const WS_CONNECTION_TIMEOUT = 5000; // 5 seconds

// Global variable to prevent multiple instances from initializing
// This is needed because the component is mounted in multiple places in the app
let isGloballyInitialized = false;
let lastInitializedUserId: string | null = null; // Track the user ID that initialized the system

/**
 * NotificationInitializer component
 * 
 * Responsible for initializing and maintaining the notification system.
 * Waits for all dependencies to be ready before initializing:
 * - User authentication
 * - WebSocket connection (or falls back to HTTP polling if not available)
 */
export function NotificationInitializer() {
  const logger = useLogger()
  const { handleNewNotification } = useNotificationStore()
  const fetchUnreadNotifications = useNotificationStore(state => state.fetchUnreadNotifications)
  const initializeAllNotifications = useNotificationStore(state => state.initializeAllNotifications)
  const userId = useUserStore(state => state.user?._id)
  const user = useUserStore(state => state.user)
  const userLoading = useUserStore(state => state.loading)
  const { getMessages } = useWSMessage()
  const connectionStatus = useWebSocketStore(state => state.connectionStatus)
  const authToken = useWebSocketStore(state => state.authToken)
  
  // Add state to track initialization and polling
  const [isInitialized, setIsInitialized] = useState(false)
  const [wsInitialized, setWsInitialized] = useState(false)
  const [canInitialize, setCanInitialize] = useState(false)
  const [dependenciesReady, setDependenciesReady] = useState(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const wsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastFetchTimeRef = useRef<number>(0)
  
  // Track dependencies status
  useEffect(() => {
    // Check if all required dependencies are available
    const isUserReady = !!userId && !userLoading && !!user;
    const isAuthReady = !!authToken;
    const isWebSocketReady = connectionStatus === 'connected' || connectionStatus === 'disconnected';
    
    const allDependenciesReady = isUserReady && isAuthReady && isWebSocketReady;
    
    logger.debug('Notification dependencies status:', {
      isUserReady,
      isAuthReady, 
      isWebSocketReady,
      userId,
      userLoading,
      connectionStatus,
      hasAuthToken: !!authToken
    });
    
    if (allDependenciesReady && !dependenciesReady) {
      logger.info('All notification system dependencies are ready');
      setDependenciesReady(true);
    }
  }, [userId, userLoading, user, authToken, connectionStatus, logger, dependenciesReady]);
  
  // Check if this instance should handle initialization
  useEffect(() => {
    // Don't proceed unless dependencies are ready
    if (!dependenciesReady || !userId) return;
    
    // Reset global initialization if the user changes
    if (lastInitializedUserId && lastInitializedUserId !== userId) {
      isGloballyInitialized = false;
      lastInitializedUserId = null;
      logger.info('NotificationInitializer: User changed, resetting initialization');
    }
    
    // Only one instance should initialize notifications
    if (!isGloballyInitialized) {
      isGloballyInitialized = true;
      lastInitializedUserId = userId;
      setCanInitialize(true);
      logger.info('NotificationInitializer: This instance will handle notifications');
    } else {
      // Only log once per session to reduce noise
      if (!canInitialize) {
        logger.info('NotificationInitializer: Another instance is already handling notifications');
      }
    }
    
    return () => {
      // If this instance was the initializer, release the lock when unmounted
      if (canInitialize && lastInitializedUserId === userId) {
        isGloballyInitialized = false;
        lastInitializedUserId = null;
        logger.info('NotificationInitializer: Released initialization lock');
      }
    };
  }, [userId, logger, canInitialize, dependenciesReady]);
  
  // Monitor WebSocket connection status
  useEffect(() => {
    if (!userId || !canInitialize || !dependenciesReady) return;
    
    // WebSocket is now connected, clear any timeout
    if (connectionStatus === 'connected') {
      logger.info('WebSocket connected - using WebSocket for notifications');
      setWsInitialized(true);
      
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
    }
    
    // When component first mounts, set a timeout to fall back to API if WebSocket doesn't connect
    if (connectionStatus === 'connecting' && !wsInitialized && !wsTimeoutRef.current) {
      logger.info(`Waiting ${WS_CONNECTION_TIMEOUT}ms for WebSocket connection before fetching notifications via API`);
      wsTimeoutRef.current = setTimeout(() => {
        if (useWebSocketStore.getState().connectionStatus !== 'connected') {
          logger.warn('WebSocket connection timeout - falling back to API for initial notifications');
          
          try {
            initializeAllNotifications();
            setIsInitialized(true);
            lastFetchTimeRef.current = Date.now();
          } catch (error) {
            logger.error('Error initializing notifications via API fallback', { error });
          }
        }
      }, WS_CONNECTION_TIMEOUT);
    }
    
    return () => {
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
    };
  }, [userId, connectionStatus, wsInitialized, canInitialize, logger, initializeAllNotifications, dependenciesReady]);
  
  // Process notifications from the unified message store
  useEffect(() => {
    if (!userId || !canInitialize || !dependenciesReady) return;
    
    // Track previous messages to detect new ones
    let prevMessageCount = useWebSocketStore.getState().messages.length;
    
    // Create a map to track recent notification content hashes
    const recentNotifications = new Map<string, {timestamp: number, contentHash: string}>();
    
    // Function to create a content hash from notification properties to detect duplicates
    const createContentHash = (notification: any): string => {
      return `${notification.type}:${notification.chatId}:${notification.message}:${notification.userName}`;
    };
    
    // Subscribe to incoming notification messages from the store
    const unsubscribe = useWebSocketStore.subscribe((state) => {
      const messages = state.messages;
      
      // Only process if we have new messages
      if (messages.length <= prevMessageCount) {
        prevMessageCount = messages.length;
        return;
      }
      
      // Find the latest notification messages
      const newMessages = messages.slice(prevMessageCount);
      prevMessageCount = messages.length;
      
      const notificationMessages = newMessages.filter(msg => 
        msg.type === 'notification' || 
        msg.type === 'newNotification'
      );
      
      if (notificationMessages.length > 0) {
        // If we receive notifications via WebSocket, consider the system initialized
        if (!isInitialized) {
          setIsInitialized(true);
          logger.info('Notification system initialized via WebSocket');
        }
        
        // Process each notification
        notificationMessages.forEach((msg: WebSocketMsg) => {
          try {
            const notification = msg.data;
            const now = Date.now();
            const contentHash = createContentHash(notification);
            
            // Check for functional duplicate by content hash within a short time window
            let isDuplicate = false;
            for (const [id, data] of recentNotifications.entries()) {
              // If same content appeared within 5 seconds, consider it a duplicate
              if (data.contentHash === contentHash && now - data.timestamp < 5000) {
                logger.debug(`Skipping duplicate notification: ${notification._id} (similar to ${id})`);
                isDuplicate = true;
                break;
              }
            }
            
            if (isDuplicate) return;
            
            // Add to recent notifications tracking
            recentNotifications.set(notification._id, {
              timestamp: now,
              contentHash
            });
            
            // Clean up old entries to prevent memory leaks
            // Only keep notifications from the last 10 seconds
            for (const [id, data] of recentNotifications.entries()) {
              if (now - data.timestamp > 10000) {
                recentNotifications.delete(id);
              }
            }
            
            logger.debug('Processing notification via WebSocket');
            handleNewNotification(notification);
            lastFetchTimeRef.current = Date.now(); // Update last fetch time
          } catch (error) {
            logger.error('Error processing notification from WebSocket', { error });
          }
        });
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [userId, handleNewNotification, logger, isInitialized, canInitialize, dependenciesReady]);
  
  // Set up polling fallback when WebSocket is disconnected
  useEffect(() => {
    if (!userId || !canInitialize || !dependenciesReady) return;
    
    const startPolling = () => {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      logger.info('Starting notification polling fallback (WebSocket disconnected)');
      pollingIntervalRef.current = setInterval(() => {
        // Only fetch if it's been at least FALLBACK_POLLING_INTERVAL since the last fetch
        const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
        if (timeSinceLastFetch >= FALLBACK_POLLING_INTERVAL) {
          logger.debug('Fetching notifications via polling fallback');
          
          try {
            fetchUnreadNotifications().then(() => {
              lastFetchTimeRef.current = Date.now();
            }).catch(error => {
              logger.error('Error fetching notifications via polling', { error });
            });
          } catch (error) {
            logger.error('Error initiating notification fetch', { error });
          }
        } else {
          logger.debug(`Skipping notification poll (last fetch was ${Math.round(timeSinceLastFetch/1000)}s ago)`);
        }
      }, FALLBACK_POLLING_INTERVAL);
    };
    
    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        logger.info('Stopping notification polling (WebSocket connected)');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    
    // Start or stop polling based on connection status
    if (connectionStatus === 'disconnected') {
      // If we've never initialized notifications and WebSocket disconnected, fetch now
      if (!isInitialized) {
        logger.info('WebSocket disconnected before initialization - falling back to API for initial notifications');
        
        try {
          initializeAllNotifications();
          setIsInitialized(true);
          lastFetchTimeRef.current = Date.now();
        } catch (error) {
          logger.error('Error initializing notifications via API on WebSocket disconnection', { error });
        }
      }
      startPolling();
    } else {
      stopPolling();
    }
    
    return () => {
      stopPolling();
    };
  }, [connectionStatus, userId, fetchUnreadNotifications, initializeAllNotifications, logger, isInitialized, canInitialize, dependenciesReady]);

  // When all dependencies are ready and we can initialize, kick off the initialization
  useEffect(() => {
    if (dependenciesReady && canInitialize && !isInitialized && userId) {
      logger.info('Starting notification initialization');
      
      // If WebSocket is connected, wait for messages
      // Otherwise, initialize via HTTP API right away
      if (connectionStatus === 'connected') {
        logger.info('WebSocket is connected, waiting for notification messages');
        // Initialization will happen when WebSocket messages arrive
      } else {
        logger.info('WebSocket not connected, initializing notifications via API');
        try {
          initializeAllNotifications();
          setIsInitialized(true);
          lastFetchTimeRef.current = Date.now();
        } catch (error) {
          logger.error('Error initializing notifications', { error });
        }
      }
    }
  }, [dependenciesReady, canInitialize, isInitialized, userId, connectionStatus, initializeAllNotifications, logger]);

  return null;
}

export default NotificationInitializer
